import { NodeType } from 'shared/types/ingestion'
import {
  Box,
  Button,
  MenuItem,
  Select,
  TextareaAutosize,
  TextField,
} from '@mui/material'
import React, { ChangeEvent, useEffect, useState } from 'react'
import { INode } from 'shared/types/tree'
import { ConfirmDialog } from 'shared/ui'
import { NodeParamsData } from '../nodeUpdates/types'
import { createNewNode } from './utility'

const availableTypesForCreation = [
  NodeType.Unit,
  NodeType.NodeList,
  NodeType.Note,
]

type CreationParams = {
  node: INode
  onCreate?: () => void
}

// This component is responsible for creating a child node for the given node
export function NodeCreation({ node, onCreate }: CreationParams) {
  const [creating, setCreating] = useState(false)
  const [nodeType, setNodeType] = useState(availableTypesForCreation[0])
  const [nodeData, setNodeData] = useState<NodeParamsData>({})
  const [nextSibling, setNextSibling] = useState<INode>()

  async function doCreate() {
    onCreate?.()
    setCreating(false)
  }

  useEffect(() => {
    setNextSibling(undefined)
  }, [node])

  return (
    <>
      <Select
        value={nodeType}
        onChange={({ target: { value } }) => setNodeType(value as NodeType)}
      >
        {availableTypesForCreation.map((type) => (
          <MenuItem key={type} value={type}>
            {type}
          </MenuItem>
        ))}
      </Select>
      <NextSibling
        parentNode={node}
        onChange={setNextSibling}
        nextSibling={nextSibling}
      />
      <NodeData
        nodeType={nodeType}
        nodeData={nodeData}
        onDataChange={setNodeData}
      />
      <Button onClick={() => setCreating(true)}>Create</Button>
      <ConfirmationComponent
        showDialog={creating}
        node={node}
        nodeType={nodeType}
        nodeData={nodeData}
        nextSibling={nextSibling}
        onCreate={doCreate}
        onCancel={() => setCreating(false)}
      />
    </>
  )
}

function NextSibling({
  parentNode,
  nextSibling,
  onChange,
}: {
  parentNode: INode
  nextSibling?: INode
  onChange(s: INode): void
}) {
  if (!parentNode.nodes?.length) return null
  return (
    <Box>
      {nextSibling ? 'Comes Before:' : 'Last Child'}
      <Select value={nextSibling?.id ?? ''}>
        {parentNode.nodes.map((n) => {
          return (
            <MenuItem key={n.id} value={n.id} onClick={() => onChange(n)}>
              {n.title}
            </MenuItem>
          )
        })}
      </Select>
    </Box>
  )
}

function NodeData({
  nodeType,
  nodeData,
  onDataChange,
}: {
  nodeType: NodeType
  nodeData: NodeParamsData
  onDataChange: React.Dispatch<React.SetStateAction<NodeParamsData>>
}) {
  const onChange = (data: NodeParamsData) => {
    onDataChange((prev) => {
      return { ...prev, ...data, type: nodeType }
    })
  }

  useEffect(() => {
    onDataChange({ type: nodeType })
  }, [nodeType, onDataChange])

  let dataKeys: string[] = []
  switch (nodeType) {
    case NodeType.Note:
      dataKeys = ['title', 'bodyValue']
      break
    case NodeType.Unit:
    case NodeType.NodeList:
      dataKeys = ['title']
      break
    default:
  }
  return (
    <NodeDatas dataKeys={dataKeys} data={nodeData} onDataChange={onChange} />
  )
}

function TitleEdit({
  data,
  onDataChange,
}: {
  data: NodeParamsData
  onDataChange: (data: NodeParamsData) => void
}) {
  return (
    <Box padding="5px">
      <TextField
        value={data.title}
        onChange={({ target: { value } }) => onDataChange({ title: value })}
        label="Title"
      />
    </Box>
  )
}

function BodyValueEdit({
  data,
  onDataChange,
}: {
  data: NodeParamsData
  onDataChange: (data: NodeParamsData) => void
}) {
  const onChange = ({
    target: { value },
  }: ChangeEvent<HTMLTextAreaElement>) => {
    if (!('body' in data)) {
      data.body = {}
    }
    onDataChange({
      ...data,
      body: {
        ...data.body,
        value,
      },
    })
  }

  return (
    <Box padding="5px">
      <TextareaAutosize
        placeholder="Body Value"
        value={data.body?.value ?? ''}
        onChange={onChange}
        style={{ width: '460px', height: '200px' }}
      />
    </Box>
  )
}

function NodeDatas({
  data,
  onDataChange,
  dataKeys,
}: {
  dataKeys: string[]
  data: NodeParamsData
  onDataChange: (data: NodeParamsData) => void
}) {
  return (
    <Box>
      <Box>Node Data</Box>
      {dataKeys.reduce<JSX.Element[]>((acc, key) => {
        switch (key) {
          case 'title':
            return [
              ...acc,
              <TitleEdit key={key} data={data} onDataChange={onDataChange} />,
            ]
          case 'bodyValue':
            return [
              ...acc,
              <BodyValueEdit
                key={key}
                data={data}
                onDataChange={onDataChange}
              />,
            ]
        }
        return acc
      }, [])}
    </Box>
  )
}

function ConfirmationComponent({
  showDialog,
  node,
  nodeType,
  nodeData,
  onCreate,
  onCancel,
  nextSibling,
}: {
  node: INode
  nodeType: NodeType
  nodeData?: NodeParamsData
  onCreate: () => void
  onCancel: () => void
  showDialog: boolean
  nextSibling?: INode
}) {
  const [doingIt, setDoingIt] = useState(false)
  if (!showDialog) return null

  async function doCreate() {
    if (!nodeData) return
    setDoingIt(true)
    await createNewNode(node, nodeData, nextSibling?.id)
    onCreate()
    setDoingIt(false)
  }

  const title = doingIt
    ? 'Working, Please Wait'
    : `Create ${nodeType} Type Node`
  return (
    <ConfirmDialog
      title={title}
      onClose={onCancel}
      onConfirm={doCreate}
      canConfirm={!doingIt}
    >
      {doingIt && (
        <Box paddingBottom="10px">It might take a while to reload the tree</Box>
      )}
      <ConfirmationText
        node={node}
        nodeType={nodeType}
        nextSibling={nextSibling}
      />
    </ConfirmDialog>
  )
}

function ConfirmationText({
  node,
  nodeType,
  nextSibling,
}: {
  node: INode
  nodeType: NodeType
  nextSibling?: INode
}) {
  return (
    <Box>
      <Box>{`You are creating a new "${nodeType}" type node.`}</Box>
      <Box>{`This node will be a child of node: "${node.title}" with type: "${node.type}"`}</Box>
      <Box>
        {nextSibling
          ? `The new node will come before ${nextSibling.title}`
          : 'The new node will be the last child node of the parent.'}
      </Box>
    </Box>
  )
}
